import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TickCircle } from "iconsax-react";
import { Button, FileViewer, Prompt, ModalWrapper } from "@/shared/ui";
import { extractErrorMessage } from "@/shared/utils";
import {
  useAcknowledgeAnnualOrderMutation,
  useAcknowledgeUnpaidOrderMutation,
  useAcknowledgeMedicalOrderMutation,
  useAcknowledgeResignationOrderMutation,
  useAcknowledgeBusinessTripOrderMutation,
} from "@/features/self/requests/api";
import { useAcknowledgeHiringOrderMutation } from "@/shared/api/onboarding";
import type { ActionType } from "../types/pending.types";

interface Props {
  orderId: number | null;
  orderType: ActionType;
  documentUrl: string | null;
  unsignedDocumentUrl: string | null;
  leaveType?: string;
  onClose: () => void;
}

export default function AcknowledgeOrderModal({
  orderId,
  orderType,
  documentUrl,
  unsignedDocumentUrl,
  leaveType,
  onClose,
}: Props) {
  const { t } = useTranslation("PendingActionsPage");

  const [acknowledgeAnnual] = useAcknowledgeAnnualOrderMutation();
  const [acknowledgeUnpaid] = useAcknowledgeUnpaidOrderMutation();
  const [acknowledgeMedical] = useAcknowledgeMedicalOrderMutation();
  const [acknowledgeResignation] = useAcknowledgeResignationOrderMutation();
  const [acknowledgeHiring] = useAcknowledgeHiringOrderMutation();
  const [acknowledgeBusinessTrip] = useAcknowledgeBusinessTripOrderMutation();

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);

  async function handleAcknowledge() {
    if (isAcknowledging) return;
    setIsAcknowledging(true);

    try {
      switch (orderType) {
        case "vacation_order":
          if (leaveType === "unpaid") {
            await acknowledgeUnpaid(orderId!).unwrap();
          } else {
            await acknowledgeAnnual(orderId!).unwrap();
          }
          break;
        case "medical_order":
          await acknowledgeMedical(orderId!).unwrap();
          break;
        case "termination_order":
          await acknowledgeResignation(orderId!).unwrap();
          break;
        case "hiring_order":
          await acknowledgeHiring().unwrap();
          break;
        case "business_trip_order":
          await acknowledgeBusinessTrip(orderId!).unwrap();
          break;
        default:
          throw new Error(`Unknown order type: ${orderType}`);
      }

      setPrompt({
        title: t("acknowledge.success"),
        text: "",
        variant: "success",
      });

      setTimeout(() => onClose(), 1500);
    } catch (error) {
      setPrompt({
        title: t("acknowledge.error"),
        text: extractErrorMessage(error, "acknowledge.error", t),
        variant: "error",
      });
    } finally {
      setIsAcknowledging(false);
    }
  }

  return (
    <>
      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
        />
      )}

      <ModalWrapper onClose={onClose} width="w-lg">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h4 className="text-display-2xs content-base-primary">
              {t("acknowledge.title")}
            </h4>
            <p className="text-body-regular-md content-base-secondary">
              {t("acknowledge.message")}
            </p>
          </div>

          {(documentUrl || unsignedDocumentUrl) && (
            <div className="flex flex-col gap-2">
              <span className="text-body-regular-md content-base-secondary">
                {t("acknowledge.fileLabel")}
              </span>
              <FileViewer
                existingFileUrl={(documentUrl || unsignedDocumentUrl) as string}
                secondaryFileUrl={documentUrl && unsignedDocumentUrl ? unsignedDocumentUrl : undefined}
              />
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2 border-t surface-base-stroke">
            <Button
              variant="primary"
              size="huge"
              className="w-full"
              disabled={isAcknowledging}
              onClick={handleAcknowledge}
            >
              <TickCircle size={16} color="currentColor" />
              {isAcknowledging ? t("acknowledge.acknowledging") : t("acknowledge.confirm")}
            </Button>
            <Button
              variant="secondary"
              size="huge"
              className="w-full"
              onClick={onClose}
              disabled={isAcknowledging}
            >
              {t("acknowledge.cancel")}
            </Button>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
}
